// src/pages/SiteDiary.tsx — Daily site diary submission and review
import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_SITE_DIARIES } from '../utils/mockDataTier3'
import { MOCK_JOBS } from '../utils/mockData'
import { formatDate } from '../utils/formatters'
import { ClipboardList, Plus, ChevronRight, ArrowLeft, CheckCircle, Clock, Sun, Cloud, Download} from 'lucide-react'
import { buildDocumentHeader, buildDocumentFooter } from '../utils/documentBranding'
import { useQuery } from '@tanstack/react-query'
import { api } from '../utils/api'

// ── PDF generation ─────────────────────────────────────────────────────────

function generateSiteDiaryPDF(d: any) {
  const now  = new Date().toLocaleDateString('en-AU', { day:'2-digit', month:'long', year:'numeric' })
  const fmtT = (s: string) => s ? new Date('2000-01-01T' + s).toLocaleTimeString('en-AU', { hour:'2-digit', minute:'2-digit', hour12:false }) : s

  const activityRows = (d.activities ?? []).map((a: any, i: number) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 10px">${a.description}</td>
      <td style="padding:5px 8px">${a.location}</td>
      <td style="padding:5px 8px;text-align:center">${a.hoursSpent}h</td>
      <td style="padding:5px 8px;text-align:center">${a.workersAssigned}</td>
      <td style="padding:5px 8px;text-align:center">${a.progress}%</td>
    </tr>`).join('')

  const personnelRows = (d.personnel ?? []).map((p: any, i: number) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 10px;font-weight:bold">${p.name}</td>
      <td style="padding:5px 8px">${p.role}</td>
      <td style="padding:5px 8px;text-align:center">${fmtT(p.start)}</td>
      <td style="padding:5px 8px;text-align:center">${fmtT(p.end)}</td>
      <td style="padding:5px 8px;text-align:center">${p.hours}h</td>
    </tr>`).join('')

  const conditionColor = d.workAreaCondition === 'SUITABLE' ? '#166534' : d.workAreaCondition === 'UNSUITABLE' ? '#b91c1c' : '#92400e'

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Site Diary — ${d.jobNumber} — ${formatDate(d.date)}</title>
<style>
*{box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:18mm 20mm}
.doc-title{border-bottom:3px solid #1F3864;padding-bottom:8px;margin-bottom:14px}
h2{font-size:14pt;font-weight:900;color:#1F3864;margin:0 0 2px;text-transform:uppercase}
.subtitle{font-size:9.5pt;color:#555}
.sec-hdr{font-size:10.5pt;font-weight:bold;color:#1F3864;border-left:4px solid #2E75B6;padding-left:8px;margin:14px 0 8px;text-transform:uppercase;letter-spacing:.5px}
.meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #ccc;margin-bottom:14px}
.mc{padding:5px 10px;border-bottom:1px solid #eee;font-size:9pt}
.mc:nth-child(odd){background:#f7f8fa;font-weight:bold;color:#555}
.condition-box{padding:8px 14px;border-radius:4px;margin-bottom:12px;font-size:9.5pt;font-weight:bold;color:${conditionColor};background:${d.workAreaCondition==='SUITABLE'?'#dcfce7':d.workAreaCondition==='UNSUITABLE'?'#fee2e2':'#fef3c7'};border:1.5px solid ${conditionColor}}
.note-box{background:#f9fafb;border:1px solid #e0e0e0;border-radius:3px;padding:10px 12px;font-size:10pt;line-height:1.55;white-space:pre-wrap;margin-bottom:12px}
.issue-box{background:#fffbeb;border:1.5px solid #f59e0b;border-radius:3px;padding:10px 12px;font-size:10pt;line-height:1.55;margin-bottom:12px;color:#92400e}
table{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:12px}
th{background:#1F3864;color:#fff;padding:5px 8px;text-align:left}
.sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:20px}
.sig-box{border:1.5px solid #999;border-radius:4px;padding:10px 14px;min-height:70px}
.sig-lbl{font-size:8pt;font-weight:bold;color:#555;text-transform:uppercase;margin-bottom:4px}
.sig-line{border-bottom:1px solid #555;height:24px;margin:8px 0 4px}
.sig-name{font-size:8pt;color:#777}
@media print{body{margin:14mm}}
</style></head><body>
${buildDocumentHeader('INVOICE')}
<div class="doc-title">
  <h2>Site Diary</h2>
  <div class="subtitle">Daily record of site activities, personnel, and conditions</div>
</div>

<div class="meta-grid">
  <div class="mc">Date</div>           <div class="mc">${formatDate(d.date)}</div>
  <div class="mc">Job number</div>     <div class="mc">${d.jobNumber}</div>
  <div class="mc">Submitted by</div>   <div class="mc">${d.submittedByName}</div>
  <div class="mc">Status</div>         <div class="mc">${d.status}</div>
  <div class="mc">Weather</div>        <div class="mc">${d.weatherConditions}</div>
  <div class="mc">Site accessible</div><div class="mc">${d.siteAccessible ? '✓ Yes' : '✗ No'}</div>
</div>

<div class="condition-box">
  Work area condition: ${d.workAreaCondition.replace('_', ' ')}
</div>

<div class="sec-hdr">Progress notes</div>
<div class="note-box">${d.progressNotes || '(None recorded)'}</div>

${d.issuesOrDelays ? `<div class="sec-hdr">Issues or delays</div>
<div class="issue-box">⚠ ${d.issuesOrDelays}</div>` : ''}

${d.materialsReceived ? `<div class="sec-hdr">Materials received</div>
<div class="note-box">${d.materialsReceived}</div>` : ''}

${activityRows ? `<div class="sec-hdr">Site activities</div>
<table><thead><tr>
  <th>Description</th><th>Location</th><th>Hours</th><th>Workers</th><th>Progress</th>
</tr></thead><tbody>${activityRows}</tbody></table>` : ''}

${personnelRows ? `<div class="sec-hdr">Personnel on site</div>
<table><thead><tr>
  <th>Name</th><th>Role</th><th>Start</th><th>Finish</th><th>Hours</th>
</tr></thead><tbody>${personnelRows}</tbody></table>` : ''}

<div class="sig-grid">
  <div class="sig-box">
    <div class="sig-lbl">Prepared by (site supervisor)</div>
    <div class="sig-line">${d.submittedByName ? '✓ Signed electronically' : ''}</div>
    <div class="sig-name">${d.submittedByName}</div>
    <div class="sig-name" style="margin-top:4px">Date: ${formatDate(d.date)}</div>
  </div>
  <div class="sig-box">
    <div class="sig-lbl">Reviewed by (PM)</div>
    <div class="sig-line">${d.status === 'REVIEWED' ? '✓ Reviewed' : ''}</div>
    <div class="sig-name">${d.status === 'REVIEWED' ? 'Reviewed' : 'Pending review'}</div>
  </div>
</div>

${buildDocumentFooter('INVOICE', 'DIARY-' + d.jobNumber + '-' + d.date.replace(/-/g,''))}
</body></html>`

  const w = window.open('', '_blank', 'width=960,height=800')
  if (!w) { alert('Pop-up blocked — allow pop-ups to generate PDFs'); return }
  w.document.write(html)
  w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

interface DiaryActivity {
  description: string
  location: string
  hoursSpent: number
  workersAssigned: number
  progress: number
}

interface DiaryPerson {
  name: string
  role: string
  start: string
  end: string
  hours: number
}

interface Diary {
  id: string
  jobId: string
  jobNumber: string
  submittedById: string
  submittedByName: string
  date: string
  status: 'SUBMITTED' | 'REVIEWED'
  weatherConditions: string
  siteAccessible: boolean
  workAreaCondition: 'SUITABLE' | 'UNSUITABLE' | 'RESTRICTED'
  progressNotes: string
  issuesOrDelays: string | null
  materialsReceived: string | null
  materialsUsed: string | null
  activities: DiaryActivity[]
  personnel: DiaryPerson[]
  reviewedById: string | null
  reviewedAt: string | null
}

const WA_CONDITION: Record<string, { label: string; bg: string; text: string }> = {
  SUITABLE:   { label: 'Suitable',   bg: 'bg-green-100',  text: 'text-green-700' },
  UNSUITABLE: { label: 'Unsuitable', bg: 'bg-red-100',    text: 'text-red-700' },
  RESTRICTED: { label: 'Restricted', bg: 'bg-amber-100',  text: 'text-amber-700' },
}

function DiaryDetail({ diary, onBack }: { diary: Diary; onBack: () => void }) {
  const { isTier } = useAuthStore()
  const wac = WA_CONDITION[diary.workAreaCondition]
  const totalHours = diary.personnel.reduce((s, p) => s + p.hours, 0)

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4"><ArrowLeft className="w-4 h-4" />Site diaries</button>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-5">
        <div className="px-6 py-5 border-b">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-bold text-gray-900 text-lg">{formatDate(diary.date)}</span>
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{diary.jobNumber}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${diary.status === 'REVIEWED' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{diary.status === 'REVIEWED' ? 'Reviewed' : 'Submitted'}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${wac.bg} ${wac.text}`}>{wac.label}</span>
              </div>
              <p className="text-sm text-gray-500">{diary.submittedByName} · {diary.weatherConditions}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => generateSiteDiaryPDF(diary)} className="btn-secondary text-xs flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" />Export PDF
              </button>
              {isTier('B_PM') && diary.status === 'SUBMITTED' && (
                <button className="btn-primary text-xs flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" />Mark reviewed</button>
              )}
            </div>
          </div>
        </div>

        {/* Progress / activities */}
        <div className="px-6 py-4 border-b">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Progress notes</h3>
          <p className="text-sm text-gray-800">{diary.progressNotes}</p>
          {diary.issuesOrDelays && <div className="mt-3 p-3 bg-amber-50 rounded-lg text-sm text-amber-800"><strong>Issues / delays:</strong> {diary.issuesOrDelays}</div>}
        </div>

        {/* Activities */}
        <div className="px-6 py-4 border-b">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Work activities</h3>
          <div className="space-y-2">
            {diary.activities.map((a, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">{i+1}</div>
                <div className="flex-1">
                  <span className="font-medium text-gray-900">{a.description}</span>
                  <span className="text-gray-400 mx-1.5">·</span>
                  <span className="text-gray-500">{a.location}</span>
                  <div className="text-xs text-gray-400 mt-0.5">{a.hoursSpent}h · {a.workersAssigned} worker{a.workersAssigned !== 1 ? 's' : ''} · {a.progress}% complete</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Personnel */}
        <div className="px-6 py-4 border-b">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Personnel on site — {totalHours.toFixed(1)}h total</h3>
          <div className="grid grid-cols-2 gap-2">
            {diary.personnel.map((p, i) => (
              <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
                <div className="font-medium text-gray-900">{p.name}</div>
                <div className="text-xs text-gray-500">{p.role} · {p.start}–{p.end} · {p.hours}h</div>
              </div>
            ))}
          </div>
        </div>

        {/* Materials */}
        {(diary.materialsReceived || diary.materialsUsed) && (
          <div className="px-6 py-4">
            {diary.materialsReceived && <div className="text-sm mb-1"><span className="text-gray-500">Materials received:</span> {diary.materialsReceived}</div>}
            {diary.materialsUsed && <div className="text-sm"><span className="text-gray-500">Materials used:</span> {diary.materialsUsed}</div>}
          </div>
        )}
      </div>
    </div>
  )
}

// Submit diary form
function SubmitDiaryForm({ jobId, onSubmit, onCancel }: { jobId: string; onSubmit: (d: Diary) => void; onCancel: () => void }) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({ weather: 'Fine, ', accessible: true, condition: 'SUITABLE', progress: '', issues: '', materialsReceived: '', materialsUsed: '' })
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))
  const job = MOCK_JOBS.find(j => j.id === jobId)!

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      id: `sd_${Date.now()}`,
      jobId, jobNumber: job.jobNumber,
      submittedById: 'e8', submittedByName: user?.fullName ?? 'Current User',
      date: new Date().toISOString().slice(0, 10),
      status: 'SUBMITTED',
      weatherConditions: form.weather,
      siteAccessible: form.accessible,
      workAreaCondition: form.condition as any,
      progressNotes: form.progress,
      issuesOrDelays: form.issues || null,
      materialsReceived: form.materialsReceived || null,
      materialsUsed: form.materialsUsed || null,
      activities: [],
      personnel: [],
      reviewedById: null, reviewedAt: null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b bg-blue-50 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Today's site diary — {job.jobNumber}</h3>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Weather conditions</label>
            <input className="input" value={form.weather} onChange={e => set('weather', e.target.value)} placeholder="e.g. Fine, 25°C, light breeze" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Work area condition</label>
            <select className="input" value={form.condition} onChange={e => set('condition', e.target.value)}>
              <option value="SUITABLE">Suitable</option>
              <option value="UNSUITABLE">Unsuitable (work stopped)</option>
              <option value="RESTRICTED">Restricted</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Progress notes <span className="text-red-500">*</span></label>
          <textarea className="input h-24 resize-none" value={form.progress} onChange={e => set('progress', e.target.value)} required placeholder="Summary of work completed today, progress against programme…" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Issues or delays (if any)</label>
          <textarea className="input h-16 resize-none" value={form.issues} onChange={e => set('issues', e.target.value)} placeholder="Any delays, problems, or matters for PM attention?" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Materials received today</label>
            <input className="input" value={form.materialsReceived} onChange={e => set('materialsReceived', e.target.value)} placeholder="e.g. Cable 250m (PO-0042)" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Materials used today</label>
            <input className="input" value={form.materialsUsed} onChange={e => set('materialsUsed', e.target.value)} placeholder="e.g. 6mm cable 40m, 2 x pit lids" />
          </div>
        </div>
        <div className="flex items-center justify-between pt-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" className="w-4 h-4 accent-blue-600" checked={form.accessible} onChange={e => set('accessible', e.target.checked)} />
            Site accessible today
          </label>
          <button type="submit" className="btn-primary">Submit diary</button>
        </div>
      </div>
    </form>
  )
}

// Main page


// ─────────────────────────────────────────────────────────────────
// PHASE 1 — SITE DIARY PDF GENERATOR
// Opens a print-ready daily site diary formatted on MCB letterhead.
// Required by TMR contracts to be submitted to superintendent daily.
// ─────────────────────────────────────────────────────────────────
function generateDiaryPDF(diary: any, jobDescription?: string) {
  const dateFmt = new Date(diary.date).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  const now     = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const cond    = { SUITABLE: 'Suitable for work', UNSUITABLE: 'Unsuitable — work stopped', RESTRICTED: 'Restricted access' }

  const personnelRows = (diary.personnel ?? []).map((p: any, i: number) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 10px">${p.name ?? p.employeeName ?? '—'}</td>
      <td style="padding:5px 10px">${p.role ?? p.trade ?? '—'}</td>
      <td style="padding:5px 10px;text-align:center">${p.hours ?? '—'}</td>
      <td style="padding:5px 10px">${p.company ?? 'MCB Civil Electrical'}</td>
    </tr>`).join('')

  const activityRows = (diary.activities ?? []).map((a: any, i: number) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 10px">${a.location ?? '—'}</td>
      <td style="padding:5px 10px">${a.description ?? a.activity ?? '—'}</td>
      <td style="padding:5px 10px;text-align:center">${a.percentComplete != null ? a.percentComplete+'%' : '—'}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Site Diary — ${diary.jobNumber} — ${diary.date}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:16mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:11pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:16px 0 8px}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:14px}
.info table{width:100%;border-collapse:collapse;margin-bottom:12px}
.info td{padding:3px 6px;font-size:10pt;vertical-align:top}
.info td:first-child{width:36%;font-weight:bold;color:#555}
table.data{width:100%;border-collapse:collapse;margin-bottom:12px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left;font-size:9.5pt}
.notes-box{border:1px solid #ccc;border-radius:4px;padding:10px 14px;background:#fafafa;margin-bottom:12px;min-height:48px}
.alert-box{border:1px solid #fbbf24;border-radius:4px;padding:10px 14px;background:#fffbeb;margin-bottom:12px}
.sig-row{display:flex;gap:40px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1px solid #333;height:28px}
.sig-lbl{font-size:8pt;color:#888;margin-top:3px}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:20px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:12mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right"><div style="font-size:18pt;font-weight:900;color:#1B3A6B">DAILY SITE DIARY</div>
    <div style="font-size:9pt;color:#888">Generated: ${now}</div></div>
</div>

<div class="info"><table>
  <tr><td>Date</td><td><strong>${dateFmt}</strong></td><td>Job / project</td><td><strong>${diary.jobNumber}</strong></td></tr>
  <tr><td>Description</td><td colspan="3">${jobDescription ?? '—'}</td></tr>
  <tr><td>Weather conditions</td><td>${diary.weatherConditions ?? '—'}</td><td>Site accessible</td><td>${diary.siteAccessible ? 'Yes' : 'No'}</td></tr>
  <tr><td>Work area condition</td><td><strong>${cond[diary.workAreaCondition as keyof typeof cond] ?? diary.workAreaCondition}</strong></td>
      <td>Status</td><td>${diary.status === 'REVIEWED' ? '✓ Reviewed by PM' : 'Submitted — awaiting PM review'}</td></tr>
  <tr><td>Submitted by</td><td>${diary.submittedByName}</td>
      <td>Reviewed by</td><td>${diary.reviewedById ? diary.reviewedByName ?? 'PM' : 'Pending'}</td></tr>
</table></div>

<h2>Progress notes</h2>
<div class="notes-box">${diary.progressNotes ?? 'No progress notes recorded.'}</div>

${diary.issuesOrDelays ? `<h2>Issues / delays</h2><div class="alert-box"><strong>⚠</strong> ${diary.issuesOrDelays}</div>` : ''}

<h2>Personnel on site</h2>
${personnelRows ? `<table class="data"><thead><tr><th>Name</th><th>Role / trade</th><th style="text-align:center">Hours</th><th>Company</th></tr></thead><tbody>${personnelRows}</tbody></table>` : '<p style="color:#888;font-size:10pt;font-style:italic">No personnel recorded against this diary entry.</p>'}

${activityRows ? `<h2>Work activities</h2><table class="data"><thead><tr><th>Location</th><th>Activity description</th><th style="text-align:center">% Complete</th></tr></thead><tbody>${activityRows}</tbody></table>` : ''}

${(diary.materialsReceived || diary.materialsUsed) ? `<h2>Materials</h2><div class="info"><table>
  ${diary.materialsReceived ? `<tr><td>Received today</td><td>${diary.materialsReceived}</td></tr>` : ''}
  ${diary.materialsUsed ? `<tr><td>Used today</td><td>${diary.materialsUsed}</td></tr>` : ''}
</table></div>` : ''}

<h2>Declaration</h2>
<p style="font-size:9.5pt;color:#555">I confirm the information recorded in this daily site diary is accurate and reflects work performed on the above project on the date stated.</p>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Site supervisor / foreman — name &amp; date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Project Manager — reviewed &amp; accepted</div></div>
</div>
<div class="footer">Confidential — for project records only · ${diary.jobNumber} · ${dateFmt} · MCB Civil Electrical · Generated ${now}</div>
</body></html>`
  const win = window.open('', '_blank', 'width=960,height=750')
  if (!win) { alert('Pop-up blocked — allow pop-ups to print diary'); return }
  win.document.write(html); win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 350)
}
export function SiteDiary() {
  const { isTier } = useAuthStore()
  const [diaries, setDiaries]   = useState<Diary[]>(MOCK_SITE_DIARIES as Diary[])
  const [selected, setSelected] = useState<Diary | null>(null)
  const [jobFilter, setJobFilter]   = useState('ALL')
  const [showForm, setShowForm]     = useState(false)
  const [formJobId, setFormJobId]   = useState(MOCK_JOBS[0].id)

  // Live site diary count from Neon
  const { data: liveDiaryRes } = useQuery({
    queryKey: ['site-diary'],
    queryFn:  async () => {
      const { data } = await api.get('/api/whs/site-diary')
      return data as any
    },
    staleTime: 60_000,
  })
  const liveDiaryCount: number = (liveDiaryRes as any)?.count ?? 0

  if (selected) return <DiaryDetail diary={selected} onBack={() => setSelected(null)} />

  const filtered = diaries.filter(d => jobFilter === 'ALL' || d.jobId === jobFilter)

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Diary</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Daily site records · {diaries.length} entries
            {liveDiaryCount > 0 && <span className="ml-2 text-blue-600 font-medium">· {liveDiaryCount} live from Neon</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {selected && (
            <button onClick={() => generateDiaryPDF(selected)} className="btn-secondary flex items-center gap-1.5 text-sm">
              <Download className="w-4 h-4" />Print diary
            </button>
          )}
          {isTier('D_FIELD') && !showForm && (
            <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" />Submit today's diary</button>
          )}
        </div>
      </div>

      {showForm && (
        <div>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Select job</label>
            <select className="input w-auto" value={formJobId} onChange={e => setFormJobId(e.target.value)}>
              {MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status)).map(j => (
                <option key={j.id} value={j.id}>{j.jobNumber} — {j.description}</option>
              ))}
            </select>
          </div>
          <SubmitDiaryForm jobId={formJobId} onCancel={() => setShowForm(false)}
            onSubmit={d => {
              setDiaries(prev => [d, ...prev])
              setShowForm(false)
              // Persist to Neon (fire-and-forget)
              api.post('/api/whs/site-diary', {
                jobId:          d.jobId,
                entryDate:      d.date,
                weatherConditions: d.weatherConditions,
                workAreaCondition: d.workAreaCondition,
                progressNotes:  d.progressNotes,
                issuesOrDelays: d.issuesOrDelays,
              }).catch((err: unknown) => console.warn('SiteDiary Neon write failed:', err))
            }} />
        </div>
      )}

      <div className="flex items-center gap-3">
        <select className="input text-sm w-auto" value={jobFilter} onChange={e => setJobFilter(e.target.value)}>
          <option value="ALL">All jobs</option>
          {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
        {filtered.length === 0
          ? <div className="py-14 text-center text-gray-400"><ClipboardList className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">No diary entries found</p></div>
          : filtered.map(d => (
              <div key={d.id} onClick={() => setSelected(d)} className="flex items-start gap-4 px-5 py-4 hover:bg-gray-50 cursor-pointer group">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  {d.workAreaCondition === 'SUITABLE' ? <Sun className="w-4 h-4 text-blue-500" /> : <Cloud className="w-4 h-4 text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-semibold text-sm text-gray-900">{formatDate(d.date)}</span>
                    <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">{d.jobNumber}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${d.status === 'REVIEWED' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{d.status === 'REVIEWED' ? 'Reviewed' : 'Submitted'}</span>
                    {d.issuesOrDelays && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Issues noted</span>}
                  </div>
                  <p className="text-sm text-gray-600 truncate">{d.progressNotes.slice(0, 90)}{d.progressNotes.length > 90 ? '…' : ''}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{d.weatherConditions} · {d.submittedByName} · {d.personnel.length} on site</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0 mt-1" />
              </div>
            ))
        }
      </div>
    </div>
  )
}
